import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { COLORS } from "./constants";
import { Sparkline } from "./Sparkline";
import { Mark } from "../Logo";

// ─────────────────────────────────────────────────────────────────────────────
// PageHeader
//
// Greeting + title on the left, the on-air panel on the right. The panel
// replaces a row of chips ("2 destinations", "0 live", a timer, a bitrate, a
// sparkline) that wrapped onto two lines and repeated what the sidebar and
// Destinations already said. The timer is the page's one big number.
//
// Every figure renders only when something real was measured: an absent value
// reads "—", never "0", because the backend sends null for "not measuring".
// On phones MobileStatBar shows the same facts as tiles, so the panel is
// skipped there.
// ─────────────────────────────────────────────────────────────────────────────

const statusLine = ({ onAir, liveCount, destinationCount }) => {
  if (onAir && liveCount > 0) return `You're live on ${liveCount} destination${liveCount === 1 ? "" : "s"}.`;
  if (onAir) return "Your encoder is connected, but no destination is receiving yet.";
  if (destinationCount > 0) return "Everything is set up. Start streaming from your encoder and Fennira sends it everywhere.";
  return "Add a destination, then start streaming from your encoder.";
};

function Stat({ label, children }) {
  return (
    <Box>
      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{label}</Typography>
      <Typography component="div" sx={{
        mt: 0.25, color: COLORS.text, fontWeight: 600, fontSize: 19, lineHeight: 1.2,
        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"
      }}>
        {children}
      </Typography>
    </Box>
  );
}

export function PageHeader({
  fullName, destinationCount, liveCount, bitrate, uptimeLabel, bitrateHistory, onAir, isMobile
}) {
  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={3}
      justifyContent="space-between" alignItems={{ lg: "stretch" }}>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", minWidth: 0 }}>
        {/* The teal triangle is the one above the "i" in the Fennira wordmark. */}
        {/* Phones show only the title: the stat tiles under it carry the
            status, and every line here pushed the picture further down. */}
        <Typography sx={{
          display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1,
          color: COLORS.muted, fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
          "&::before": {
            content: '""', width: 0, height: 0, flexShrink: 0,
            borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
            borderBottom: `8px solid ${COLORS.cyan}`
          }
        }}>
          Welcome back{fullName ? `, ${fullName}` : ""}
        </Typography>
        <Typography component="h1" sx={{
          mt: { xs: 0, sm: 1.25 }, fontFamily: COLORS.fontDisplay, fontWeight: 700,
          fontSize: { xs: 26, sm: 34, md: 48 }, lineHeight: { xs: 1.1, sm: 1.04 }, letterSpacing: "-0.025em", color: COLORS.text
        }}>
          Broadcast Control
        </Typography>
        <Typography sx={{ display: { xs: "none", sm: "block" }, mt: 1.25, color: COLORS.textSoft, fontSize: 15.5, lineHeight: 1.55, maxWidth: 480 }}>
          {statusLine({ onAir, liveCount, destinationCount })}
        </Typography>
      </Box>

      {!isMobile && (
        <Box component="section" aria-label="Broadcast status" sx={{
          position: "relative", overflow: "hidden", flex: { lg: "0 1 520px" },
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3.5,
          px: 3.5, py: 3, borderRadius: "22px", bgcolor: COLORS.panel,
          border: `1px solid ${onAir ? "rgba(229,72,77,0.40)" : COLORS.border}`
        }}>
          <Mark size={210} sx={{
            position: "absolute", right: -28, bottom: -48, opacity: 0.05, pointerEvents: "none"
          }} />

          <Box sx={{ position: "relative" }}>
            {onAir ? (
              <Box component="span" sx={{
                display: "inline-flex", alignItems: "center", gap: 1, height: 26, px: 1.4, borderRadius: "7px",
                bgcolor: COLORS.dangerFill, color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em"
              }}>
                <Box component="span" sx={{
                  width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor",
                  animation: "pulse 1.6s ease-in-out infinite",
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" }
                }} />
                ON AIR
              </Box>
            ) : (
              <Box component="span" sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75, height: 24, px: 1.25, borderRadius: "999px",
                border: `1px solid ${COLORS.borderStrong}`, color: COLORS.muted,
                fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase"
              }}>
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor" }} />
                Off air
              </Box>
            )}
            <Typography sx={{
              mt: 1.5, color: COLORS.text, fontWeight: 600, fontSize: 52, lineHeight: 1,
              letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums"
            }}>
              {uptimeLabel || "00:00:00"}
            </Typography>
            <Typography sx={{ mt: 1, color: COLORS.muted, fontSize: 13 }}>
              {onAir ? "Since your encoder connected" : "Not broadcasting"}
            </Typography>
          </Box>

          <Stack spacing={1.75} sx={{
            position: "relative", flex: "1 1 160px", pl: 3, borderLeft: `1px solid ${COLORS.border}`
          }}>
            <Stat label="Destinations live">
              {destinationCount ? `${liveCount} of ${destinationCount}` : "—"}
            </Stat>
            <Stat label="Bitrate out">
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <span>
                  {bitrate != null ? (bitrate / 1000).toFixed(2) : "—"}
                  <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, color: COLORS.muted }}>Mbps</Box>
                </span>
                {/* Hidden below two samples: a lone point next to a live figure
                    just looks broken. */}
                {bitrateHistory?.length > 1 && (
                  <Sparkline values={bitrateHistory} ariaLabel="Recent output bitrate trend" />
                )}
              </Stack>
            </Stat>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
